import {
  Button,
  Divider,
  Grid,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { IconPrinter } from "@tabler/icons-react";
import { Loading, PageHeader } from "components";
import { useAbility } from "configs/casl";
import PageContent from "components/layouts/PageContent";
import CustomTable from "components/ui/CustomTable";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomText from "components/ui/CustomText/CustomText";
import { MRT_ColumnDef } from "mantine-react-table";
import {
  canEditSalesOrderItems,
  SALES_ORDER_EDIT_MODE,
  SALES_ORDER_STATUS_COLOR,
  SALES_ORDER_STATUS_LABEL,
} from "constants/common/salesOrder";
import { SalesOrderItemResponseType } from "constants/types/salesOrder";
import { ReactNode, useMemo } from "react";
import { formatCurrency, formatDate } from "utils/helpers/format";
import { useParams, useSearchParams } from "react-router-dom";
import { SALES_ORDER_PAGE } from "routes/route.constant";
import SalesOrderItemsEditForm from "./components/SalesOrderItemsEditForm/SalesOrderItemsEditForm";
import { useSalesOrderDetail } from "utils/hooks/request/useSalesOrder";
import { useUserMap } from "utils/hooks/request/useUsers";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import { SALES_ORDER_PAGE_TITLE } from "pages/SalesOrder/config";
import SalesOrderPrintContent from "pages/SalesOrder/components/SalesOrderPrint";

// ─── sub-components ─────────────────────────────────────────────────────────

const InfoField = ({ label, value }: { label: string; value: ReactNode }) => (
  <Stack gap={2}>
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    <Text size="sm" fw={500}>
      {value ?? "—"}
    </Text>
  </Stack>
);

const SectionTitle = ({ children }: { children: string }) => (
  <CustomText variant="section-title">{children}</CustomText>
);

// ─── items table ─────────────────────────────────────────────────────────────

const ItemsTable = ({
  items,
  totalAmount,
}: {
  items: SalesOrderItemResponseType[];
  totalAmount: number;
}) => {
  const columns = useMemo<MRT_ColumnDef<SalesOrderItemResponseType>[]>(
    () => [
      {
        id: "stt",
        header: "STT",
        size: 60,
        grow: false,
      },
      {
        accessorKey: "productName",
        header: "Tên sản phẩm",
        minSize: 260,
        grow: true,
        Cell: ({ cell }) => (
          <Text size="sm" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {cell.getValue<string>()}
          </Text>
        ),
      },
      {
        accessorKey: "productUnit",
        header: "Đơn vị",
        size: 100,
        grow: false,
      },
      {
        accessorKey: "quantity",
        header: "Số lượng",
        size: 100,
        grow: false,
        mantineTableBodyCellProps: { align: "right" },
        Cell: ({ cell }) => cell.getValue<number>().toLocaleString("vi-VN"),
      },
      {
        accessorKey: "initialPrice",
        header: "Giá gốc",
        size: 130,
        grow: false,
        Cell: ({ cell }) => (
          <Text size="sm" c="dimmed">
            {formatCurrency(cell.getValue<number>())}
          </Text>
        ),
      },
      {
        accessorKey: "actualPrice",
        header: "Giá bán",
        size: 130,
        grow: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()),
        Footer: () => (
          <Text size="sm" fw={600}>
            Tổng cộng
          </Text>
        ),
      },
      {
        accessorKey: "totalAmount",
        header: "Thành tiền",
        size: 140,
        grow: false,
        Cell: ({ cell }) => (
          <Text size="sm" fw={500}>
            {formatCurrency(cell.getValue<number>())}
          </Text>
        ),
        Footer: () => (
          <Text size="sm" fw={700} c="blue">
            {formatCurrency(totalAmount)}
          </Text>
        ),
      },
    ],
    [totalAmount],
  );

  return <CustomTable columns={columns} data={items} />;
};

// ─── page ────────────────────────────────────────────────────────────────────

const DetailSalesOrder = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get("mode") === SALES_ORDER_EDIT_MODE;
  const { salesOrder, isLoading, mutate } = useSalesOrderDetail(id);
  const userMap = useUserMap();
  const { openCrudModal } = useAppModal();
  const ability = useAbility();
  // In phiếu chỉ cần quyền xem: nhân viên (sales, debt) xem được đơn thì in được.
  // Trước đây gác bằng "export" nên nút bị ẩn với mọi role không xuất Excel.
  const canPrint = ability.can("read", "sale_order");

  const handlePrint = () => {
    if (!salesOrder) return;
    openCrudModal(
      <CustomText variant="section-title">Phiếu bán hàng</CustomText>,
      {
        mode: "view",
        children: <SalesOrderPrintContent salesOrder={salesOrder} />,
      },
      "90%",
    );
  };

  return (
    <Stack gap="sm">
      <PageHeader
        breadcrumbs={[
          { title: SALES_ORDER_PAGE_TITLE, href: SALES_ORDER_PAGE },
          { title: "Chi tiết đơn bán hàng", href: "" },
        ]}
      />

      {canPrint && (
        <Group justify="flex-end">
          <Button
            leftSection={<IconPrinter size={16} />}
            variant="default"
            disabled={!salesOrder}
            onClick={handlePrint}
          >
            In / Xuất PDF
          </Button>
        </Group>
      )}

      <PageContent>
        <Loading variant="overlay" visible={isLoading}>
          {salesOrder && (
            <Stack gap="md">
              {/* ── Thông tin chung ── */}
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <SectionTitle>Thông tin đơn hàng</SectionTitle>

                  <SimpleGrid cols={{ base: 2, md: 3 }}>
                    <InfoField label="Mã đơn hàng" value={salesOrder.code} />
                    <InfoField
                      label="Kho xuất"
                      value={salesOrder.warehouse.name}
                    />
                    <InfoField
                      label="Trạng thái"
                      value={
                        <CustomBadge
                          label={
                            SALES_ORDER_STATUS_LABEL[salesOrder.status] ??
                            salesOrder.status
                          }
                          color={
                            SALES_ORDER_STATUS_COLOR[salesOrder.status] ??
                            "gray"
                          }
                        />
                      }
                    />
                    <InfoField
                      label="Tổng tiền"
                      value={
                        <Text size="sm" fw={700} c="blue">
                          {formatCurrency(salesOrder.totalAmount)}
                        </Text>
                      }
                    />
                    {salesOrder.oldDebt && salesOrder.oldDebt > 0 ? (
                      <InfoField
                        label="Công nợ cũ"
                        value={
                          <Text size="sm" fw={700} c="orange.7">
                            {formatCurrency(salesOrder.oldDebt)}
                          </Text>
                        }
                      />
                    ) : null}
                    <InfoField
                      label="Đã thanh toán"
                      value={
                        <Text size="sm" fw={700} c="green">
                          {formatCurrency(salesOrder.paidAmount)}
                        </Text>
                      }
                    />
                    <InfoField
                      label="Người tạo"
                      value={salesOrder.createdBy.fullname}
                    />
                    <InfoField
                      label="Ngày tạo"
                      value={formatDate(salesOrder.createdAt)}
                    />
                    {salesOrder.confirmedBy && (
                      <InfoField
                        label="Người xác nhận"
                        value={salesOrder.confirmedBy.fullname}
                      />
                    )}
                    {!!salesOrder.confirmedAt && (
                      <InfoField
                        label="Ngày xác nhận"
                        value={formatDate(salesOrder.confirmedAt)}
                      />
                    )}
                  </SimpleGrid>

                  {salesOrder.notes && (
                    <>
                      <Divider />
                      <Grid>
                        <Grid.Col span={{ base: 12, md: 8 }}>
                          <InfoField label="Ghi chú" value={salesOrder.notes} />
                        </Grid.Col>
                      </Grid>
                    </>
                  )}
                </Stack>
              </Paper>

              {/* ── Thông tin khách hàng ── */}
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <SectionTitle>Thông tin khách hàng</SectionTitle>

                  <SimpleGrid cols={{ base: 2, md: 3 }}>
                    <InfoField
                      label="Tên khách hàng"
                      value={salesOrder.partner.name}
                    />
                    <InfoField
                      label="Mã khách hàng"
                      value={salesOrder.partner.code}
                    />
                    <InfoField
                      label="Số điện thoại"
                      value={salesOrder.partner.phone}
                    />
                    <InfoField
                      label="Mã số thuế"
                      value={salesOrder.partner.taxCode}
                    />
                    <InfoField
                      label="Người phụ trách"
                      value={(() => {
                        // Người phụ trách của ĐƠN nếu có, không thì của đối
                        // tác — khớp với bảng danh sách, phiếu in và báo cáo.
                        const id =
                          salesOrder.contactPersonId ??
                          salesOrder.partner.contactPersonId;
                        const u = id ? userMap[id] : undefined;
                        return u ? `${u.fullname} (${u.code})` : undefined;
                      })()}
                    />
                    <InfoField
                      label="Trạng thái KH"
                      value={
                        <CustomBadge
                          label={
                            salesOrder.partner.isActive
                              ? "Đang hoạt động"
                              : "Tạm khóa"
                          }
                          color={salesOrder.partner.isActive ? "green" : "red"}
                        />
                      }
                    />
                  </SimpleGrid>
                </Stack>
              </Paper>

              {/* ── Danh sách sản phẩm ── */}
              {/* <Paper p="md" withBorder> */}
              <Stack gap="md">
                {/* <SectionTitle>Danh sách sản phẩm qqqq</SectionTitle> */}

                {isEditMode &&
                canEditSalesOrderItems(
                  salesOrder.status,
                  salesOrder.totalAmount,
                ) ? (
                  <SalesOrderItemsEditForm
                    order={salesOrder}
                    items={salesOrder.items ?? []}
                    mutate={mutate}
                  />
                ) : salesOrder.items && salesOrder.items.length > 0 ? (
                  <ItemsTable
                    items={salesOrder.items}
                    totalAmount={salesOrder.totalAmount}
                  />
                ) : (
                  <Text size="sm" c="dimmed">
                    Không có sản phẩm nào.
                  </Text>
                )}
              </Stack>
              {/* </Paper> */}
            </Stack>
          )}
        </Loading>
      </PageContent>
    </Stack>
  );
};

export default DetailSalesOrder;
